/**
 * A question, the panel, an interpretation — turn `7a`.
 *
 * Lives on the rule itself, in League › Rulebook, because that is where somebody arrives with the
 * question. Two states: OPEN, while the panel answers, and RECORDED, which is how the rule reads
 * for the rest of the season.
 *
 * WHY THIS EXISTS AT ALL. The exact question drawn — does a franchise tag limit a GM to five
 * keepers? — is answered today in a comment in `luxuryTax.ts` as "Confirmed by the commissioner,
 * 2026". A decision that lives in the code and nowhere the league can see it, and which 2015-I-4
 * says a three-person panel should have made. This is the screen that decision should have had.
 *
 * THE COMPOSITION IS THE DESIGN WORK and it is derived, never chosen — see
 * `domain/interpretation.ts`. What the screen adds is stating WHICH SEAT each man holds and why,
 * because a panel whose composition has to be argued about is a panel whose answer will be.
 */

import { useMemo, useState } from 'react';
import { managerShort } from '../domain/identity.js';
import {
  citeAs, holdsUntil, panelFor, panelTally, type Interpretation as Interp, type PanelInput,
} from '../domain/interpretation.js';
import { GmBadge } from './GmBadge.js';
import { Citations, Rule } from './RuleText.js';
import { atLeast, type Role } from '../store/types.js';

export function InterpretationPanel(
  { interp, panelInput, role, onAnswer, onRecord }: {
    interp: Interp;
    panelInput: PanelInput;
    role: Role;
    onAnswer: (gm: string, answer: string) => void;
    onRecord: (decision: string) => void;
  },
) {
  const commish = atLeast(role, 'commissioner');
  const panel = useMemo(() => panelFor(panelInput), [panelInput]);
  const tally = useMemo(
    () => panelTally(panel, interp.answers ?? {}),
    [panel, interp.answers],
  );
  const [draft, setDraft] = useState('');
  const recorded = Boolean(interp.decision);

  const conflicted = new Set(panelInput.conflicted.map((g) => g.toUpperCase()));
  const standDown = panelInput.commissioners.filter((c) => conflicted.has(c.toUpperCase()));

  return (
    <section className={`card interp ${recorded ? 'recorded' : 'open'}`}>
      <div className="ihead">
        <span className="chip">{interp.ruleId}{interp.section ? ` ${interp.section}` : ''}</span>
        <span className={`chip ${recorded ? 'good' : 'warning'}`}>
          {recorded ? 'interpretation recorded' : 'interpretation open'}
        </span>
        <Rule id={interp.ruleId}>read the rule</Rule>
      </div>

      <h3 className="iq">{interp.question}</h3>
      <p className="mut iwhen">
        Raised by {managerShort(interp.raisedBy)},{' '}
        {new Date(interp.raisedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
        {interp.blocking?.length ? (
          <>
            {' · '}
            {/*
              What is WAITING on the answer, which is the whole reason a panel exists rather than
              a committee: two trades held up is a question that cannot wait five weeks for a
              quorum of eight.
            */}
            <span className="chip warning">
              {interp.blocking.length} thing{interp.blocking.length === 1 ? '' : 's'} waiting on
              the answer
            </span>
          </>
        ) : null}
      </p>

      {/* THE PANEL. Each seat states what it is and why, cascades included. */}
      <div className="ipanel">
        <div className="iphead">
          <b>The panel</b>
          <span className="mut">
            two co-commissioners and one rotating GM — composition is derived, not chosen
          </span>
          <Citations ids={['2015-I-4']} />
        </div>

        {standDown.map((c) => (
          <div className="iseat out" key={c}>
            <GmBadge gm={c} />
            <span className="iname">{managerShort(c)}</span>
            <span className="chip warning">stands to benefit</span>
            <span className="mut iwhy">
              Recused, so the seat passes to the most recent lottery winner.
            </span>
          </div>
        ))}

        {panel.map((s) => {
          const answer = (interp.answers ?? {})[s.gm.toUpperCase()];
          return (
            <div className={`iseat ${answer ? 'answered' : ''}`} key={s.gm}>
              <GmBadge gm={s.gm} />
              <span className="iname">{managerShort(s.gm)}</span>
              <span className="mut iwhy">{s.why}</span>
              {answer
                ? <span className="chip good ians">{answer}</span>
                : <span className="mut ians">not answered</span>}
              {commish && !recorded && (
                <input className="ianswer" placeholder="record his reading"
                  aria-label={`${managerShort(s.gm)}’s reading`}
                  defaultValue={answer ?? ''}
                  onBlur={(e) => e.target.value.trim()
                    && onAnswer(s.gm.toUpperCase(), e.target.value.trim())} />
              )}
            </div>
          );
        })}
        <p className="note">
          Had a co-commissioner taken the first overall pick, or the lowest point total, the
          rotating seat would move down the point standings instead. The app works that cascade
          out and shows the seat it landed on.
        </p>
      </div>

      {!recorded ? (
        <div className="idecide">
          <div className="iwhy2">
            <b>{tally.decidable ? 'Two of three, so it can be recorded' : 'Still open'}</b>
            <p className="note">{tally.why}</p>
          </div>
          {commish && (
            <div className="actions">
              <input className="idraft" placeholder="the interpretation, as it will be cited"
                value={draft || tally.leading || ''}
                onChange={(e) => setDraft(e.target.value)} />
              <button type="button" className="btn" disabled>Wait</button>
              <button type="button" className="btn primary" disabled={!tally.decidable}
                title={tally.decidable ? undefined : tally.why}
                onClick={() => onRecord(draft || tally.leading || '')}>
                Record the interpretation
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="idone">
          <div className="ifield">
            <span className="efflabel">Recorded</span>
            <p>{interp.decision}</p>
          </div>
          <div className="ifield">
            <span className="efflabel">Holds until</span>
            <p>{holdsUntil(interp)}</p>
          </div>
          <div className="ifield">
            <span className="efflabel">Cites as</span>
            <p><code>{citeAs(interp)}</code>
              <span className="mut"> shows under the rule wherever it is cited</span></p>
          </div>
          {/*
            THEN CODIFIED. The panel answers for a season; only the committee writes rule text, so
            an interpretation goes on the next docket automatically rather than quietly hardening
            into a rule nobody voted for.
          */}
          <div className="ifield">
            <span className="efflabel">Then codified</span>
            <p>
              A draft proposal for {interp.season + 1} was written when this was recorded — on
              League › Proposals, under the commissioners&rsquo; drafts. The panel answers for a
              season; only the committee writes rule text, so it lands where the wording can be
              worked on before the deadline rather than arriving pre-submitted.
            </p>
          </div>
        </div>
      )}

      <div className="iwhynot">
        <b>Why not the committee</b>
        <p className="note">
          A quorum of eight and a super-majority of seven is right for writing rules and wrong for
          answering a question in the middle of Week 7. Three panelists, most votes wins, and the
          answer holds for the rest of this season only.
        </p>
        <Citations ids={['2015-I-4', '2015-I-1']} />
      </div>
    </section>
  );
}
